import axios from "axios";
// Import SignedInSessionResource for the correct session type
import type { SignedInSessionResource } from "@clerk/types";
import { getUnexpiredToken } from "@/utils/utils";
import { showFileConflictModal } from "@/store/zustand/FileConflictModal";

export interface UploadFileParams {
  file: File;
  // Use SignedInSessionResource for the session type
  session: SignedInSessionResource | null;
  canvasId: string; // This ID is used in the endpoint URL.
  forUploadToCodebase?: boolean;
  /** Explicit directory path for codebase uploads, relative to project root. E.g., "src/components/" */
  codebaseDirectory?: string;
}

export interface UploadFileResult {
  filePath: string; // Full path of the uploaded file relative to project root
  fileName: string; // Name of the file
  directory: string; // Directory where the file was uploaded, relative to project root
}

export async function replaceFileInServer(
  params: UploadFileParams,
): Promise<UploadFileResult> {
  const { file, session, canvasId, forUploadToCodebase, codebaseDirectory } =
    params;

  if (!session) {
    throw new Error("User session is not available for file replacement.");
  }

  let finalDirectoryToUploadTo: string;

  if (forUploadToCodebase) {
    if (file.type.startsWith("image/")) {
      finalDirectoryToUploadTo = "public/images/";
    } else if (codebaseDirectory) {
      finalDirectoryToUploadTo = codebaseDirectory; // Use provided path
    } else {
      // Default for unexpected non-image codebase uploads if codebaseDirectory not set
      console.warn(
        `No specific directory for codebase file type: ${file.type}. Defaulting to 'uploads/'.`,
      );
      finalDirectoryToUploadTo = "uploads/"; // A generic uploads directory at the root
    }
  } else {
    // Original logic for non-codebase uploads (e.g., to public/images/ for canvas assets)
    finalDirectoryToUploadTo = "public/images/";
  }

  // Normalize directory path: ensure trailing slash, ensure relative path from project root
  if (finalDirectoryToUploadTo && !finalDirectoryToUploadTo.endsWith("/")) {
    finalDirectoryToUploadTo += "/";
  }
  if (finalDirectoryToUploadTo.startsWith("/")) {
    finalDirectoryToUploadTo = finalDirectoryToUploadTo.substring(1);
  }
  // Handle root directory case, ensuring it's an empty string if it was '/'
  if (finalDirectoryToUploadTo === "/") {
    finalDirectoryToUploadTo = "";
  }

  const supabaseJWT = await getUnexpiredToken(session);
  if (!supabaseJWT) {
    throw new Error("Failed to get Supabase JWT for upload authorization.");
  }

  const fileName = `${finalDirectoryToUploadTo}${file.name}`;

  // For binary files (like images), use the file endpoint with base64 content
  if (file.type.startsWith("image/") || !file.type.startsWith("text/")) {
    // Read file as base64 for binary files
    const fileContents = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(",")[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const response = await axios.post(
      `${import.meta.env.VITE_BACKEND_BASE_URL}/canvases/${canvasId}/file`,
      {
        filename: fileName,
        fileContents: fileContents,
      },
      {
        headers: {
          Authorization: `Bearer ${supabaseJWT}`,
          "Content-Type": "application/json",
        },
      },
    );
  } else {
    // For text files, read as text
    const fileContents = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsText(file);
    });

    const response = await axios.post(
      `${import.meta.env.VITE_BACKEND_BASE_URL}/canvases/${canvasId}/file`,
      {
        filename: fileName,
        fileContents: fileContents,
      },
      {
        headers: {
          Authorization: `Bearer ${supabaseJWT}`,
          "Content-Type": "application/json",
        },
      },
    );
  }

  // The file endpoint doesn't return the same structure as createFile,
  // so we construct the expected response
  return {
    filePath: fileName,
    fileName: file.name,
    directory: finalDirectoryToUploadTo,
  };
}

export async function uploadFileToServer(
  params: UploadFileParams,
): Promise<UploadFileResult> {
  const { file, session, canvasId, forUploadToCodebase, codebaseDirectory } =
    params;

  if (!session) {
    throw new Error("User session is not available for file upload.");
  }

  let finalDirectoryToUploadTo: string;

  if (forUploadToCodebase) {
    if (file.type.startsWith("image/")) {
      finalDirectoryToUploadTo = "public/images/";
    } else if (codebaseDirectory) {
      finalDirectoryToUploadTo = codebaseDirectory; // Use provided path
    } else {
      // Default for unexpected non-image codebase uploads if codebaseDirectory not set
      console.warn(
        `No specific directory for codebase file type: ${file.type}. Defaulting to 'uploads/'.`,
      );
      finalDirectoryToUploadTo = "uploads/"; // A generic uploads directory at the root
    }
  } else {
    // Original logic for non-codebase uploads (e.g., to public/images/ for canvas assets)
    finalDirectoryToUploadTo = "public/images/";
  }

  // Normalize directory path: ensure trailing slash, ensure relative path from project root
  if (finalDirectoryToUploadTo && !finalDirectoryToUploadTo.endsWith("/")) {
    finalDirectoryToUploadTo += "/";
  }
  if (finalDirectoryToUploadTo.startsWith("/")) {
    finalDirectoryToUploadTo = finalDirectoryToUploadTo.substring(1);
  }
  // Handle root directory case, ensuring it's an empty string if it was '/'
  if (finalDirectoryToUploadTo === "/") {
    finalDirectoryToUploadTo = "";
  }

  const formData = new FormData();
  // Backend should ideally handle potential name collisions, possibly by renaming or returning an error.
  // Sending original file.name for backend to decide.
  formData.append("file", file, file.name);
  formData.append("directoryToUploadTo", finalDirectoryToUploadTo);

  if (forUploadToCodebase) {
    formData.append("forUploadToCodebase", "true");
  }

  const supabaseJWT = await getUnexpiredToken(session);
  if (!supabaseJWT) {
    throw new Error("Failed to get Supabase JWT for upload authorization.");
  }

  try {
    const response = await axios.post(
      // Using the specified endpoint structure. VITE_BACKEND_BASE_URL must be configured.
      `${
        import.meta.env.VITE_BACKEND_BASE_URL
      }/canvases/${canvasId}/createFile`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${supabaseJWT}`,
          "Content-Type": "multipart/form-data",
        },
      },
    );

    // Standardize response structure. Adapt based on actual backend response.
    // Assuming backend returns { path: "full/path/to/file.ext", name: "file.ext", directory: "full/path/to/" }
    if (response.data && response.data.path) {
      return {
        filePath: response.data.path,
        fileName: response.data.name || file.name,
        directory: response.data.directory || finalDirectoryToUploadTo,
      };
    } else {
      // Fallback if backend response is not as expected. This might be inaccurate.
      const constructedPath = `${finalDirectoryToUploadTo}${file.name}`;
      console.warn(
        `Backend did not return precise filePath. Using constructed path: ${constructedPath}`,
      );
      return {
        filePath: constructedPath,
        fileName: file.name,
        directory: finalDirectoryToUploadTo,
      };
    }
  } catch (error: any) {
    // Handle 409 (Conflict) - file already exists
    if (error.response?.status === 409) {
      const filePath = `${finalDirectoryToUploadTo}${file.name}`;

      // Show confirmation modal and wait for user decision
      const shouldReplace = await showFileConflictModal(
        file.name,
        filePath,
        async () => {
          // This function just triggers the replacement without returning the result
          await replaceFileInServer(params);
        },
      );

      if (shouldReplace) {
        // If user confirmed, perform the replacement and return the result
        return replaceFileInServer(params);
      } else {
        // User cancelled, throw a specific error
        throw new Error("File upload cancelled by user");
      }
    }

    // Re-throw other errors
    throw error;
  }
}
